import { useState, useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import QRCode from "qrcode";
import { api } from "../api";
import type { Customer } from "../types";
import { useLocalization } from "../contexts/LocalizationContext";

interface QrModalProps {
  user: Customer | null;
  onClose: () => void;
}

export default function QrModal({ user, onClose }: QrModalProps) {
  const { t } = useLocalization();
  const [qrDataUrl, setQrDataUrl] = useState("");

  useEffect(() => {
    if (user?.membershipCode) {
      QRCode.toDataURL(`${user.membershipCode}`, { width: 168, margin: 1, color: { dark: "#111", light: "#fff" } })
        .then(url => setQrDataUrl(url))
        .catch(() => {});
    }
  }, [user?.membershipCode]);

  const isAndroid = /android/i.test(navigator.userAgent);
  const handleAddToWallet = async () => {
    try {
      if (isAndroid) {
        const { data } = await api.post<{ saveUrl: string }>("/api/wallet/google/save-link");
        window.open(data.saveUrl, "_blank", "noopener,noreferrer");
      } else {
        const response = await api.get("/api/wallet/apple-pass", {
          responseType: "blob",
          headers: { Accept: "application/vnd.apple.pkpass" },
        });
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "membership.pkpass";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch {}
  };

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
      <Box onClick={onClose} sx={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)" }} />
      <Box sx={{
        position: "relative", zIndex: 1,
        width: "100%", maxWidth: "300px",
        background: "#fff", borderRadius: "20px",
        padding: "28px 24px 24px",
        boxShadow: "0 16px 48px rgba(0,0,0,0.25)",
        textAlign: "center",
      }}>
        <Box component="button" onClick={onClose} sx={{
          position: "absolute", top: "10px", right: "12px",
          width: 32, height: 32, border: "none", background: "none",
          fontSize: "22px", lineHeight: 1, color: "#888", cursor: "pointer",
          borderRadius: "8px", fontFamily: "inherit",
          "&:active": { background: "#F5F5F5" },
        }}>×</Box>

        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1A1A1A", mb: "4px" }}>
          {user?.nextMembershipLevel?.currentMembershipLevelName || "Member"}
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "#7BA88A", fontWeight: 600, mb: "18px" }}>
          {user?.pointsBalance?.toLocaleString() ?? "0"} {t("home.points.points")}
        </Typography>

        <Box sx={{ width: 200, height: 200, mx: "auto", mb: "14px", background: "#fff", border: "1px solid #EEE", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", padding: "12px" }}>
          {qrDataUrl
            ? <img src={qrDataUrl} alt="QR" style={{ width: "100%", height: "100%" }} />
            : <Typography sx={{ fontSize: 12, color: "#999" }}>{t("common.loading")}</Typography>
          }
        </Box>

        <Typography sx={{ fontSize: "12px", color: "#8A8A8A", mb: "16px" }}>
          {t("qrCode.instruction")}
        </Typography>

        <Button variant="text" onClick={handleAddToWallet} sx={{ p: 0, minHeight: "unset", "&:hover": { backgroundColor: "transparent" } }}>
          <img src={isAndroid ? "/icons/ic_google_wallet.svg" : "/icons/ic_apple_wallet.svg"} alt="" style={{ height: 40 }} />
        </Button>
      </Box>
    </Box>
  );
}
